import { DEBOUNCE_TIME } from "common/utilities/timeout/timeout";
import debounce from "debounce";
import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

function hasWindowSelection() {
  return (window.getSelection() || "").toString().length > 0;
}

interface UseShortcutOptions {
  ref?: RefObject<HTMLElement>;
  disabled?: boolean;
  disableIfWindowSelection?: boolean;
  debounceTime?: number;
  resetOnKeyUp?: boolean;
}

/**
 * Self-contained React effect listening to ONE or a suite of TWO keys
 *
 * @param shortcut
 * @param ignoreInputFieldFocused will also recognize and register keys typed if an input field is in focus. Default is true.
 * @param options.ref element listener (window by default)
 * @param options.disabled disable the shortcut, will run the original browser shortcut
 * @param options.debounceTime debounce time when using the shortcut. i.e. keyboard navigation need shorter time as it is used repeatedly. Set to 0 to disable it.
 * @param options.resetOnKeyUp only clear the shortcut on keyup. Used when you need the user to hold a key to act as a trigger.
 *
 * @returns true when the shortcut is activated, then is reset automatically to undefined
 *
 * @example ctrl+s, Enter, Escape, a-a, ArrowUp, ArrowDown etc.
 */
function useShortcut(
  shortcut: string | undefined,
  ignoreInputFieldFocused: boolean | undefined,
  options?: UseShortcutOptions,
) {
  const refEl = options?.ref && options.ref.current ? options.ref.current : window;
  const previousKey = useRef("");

  const [trigger, setTrigger] = useState<boolean | undefined>();

  const keys = (shortcut || "").replace("ctrl+", "").split("-");
  const isCtrlKeyShortcut = (shortcut || "").match("ctrl+") !== null;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedTrigger = useCallback(
    debounce(
      () => {
        setTrigger(true);
        previousKey.current = "";
      },
      options?.debounceTime === undefined ? DEBOUNCE_TIME.shortcut : options.debounceTime,
    ),
    [],
  );

  const clearTrigger = useCallback(() => {
    if (trigger) setTrigger(undefined);
  }, [trigger]);

  useEffect(() => {
    function handleKeyUp() {
      setTrigger(undefined);
    }

    function handleKeyDown(e: Event) {
      const event = e as KeyboardEvent;
      if (trigger) {
        // Prevent all ctrl+ default browser actions, like save page as html file
        if (isCtrlKeyShortcut && event.ctrlKey) {
          e.preventDefault();
        }

        return;
      }

      if (options?.disabled) return;
      if (options?.disableIfWindowSelection && hasWindowSelection()) return;
      const target = event.target ? (event.target as HTMLElement) : undefined;

      if (
        ignoreInputFieldFocused !== false ||
        (ignoreInputFieldFocused === false && target?.nodeName !== "INPUT" && target?.nodeName !== "TEXTAREA")
      ) {
        // First condition handles shortcut made of one key only (including ctrl+ combination)
        // Second conditions handle shortcut made of two keys pressed one after another
        if (
          (!keys[1] && event.key === keys[0] && (!isCtrlKeyShortcut || (isCtrlKeyShortcut && event.ctrlKey))) ||
          (keys[1] && previousKey.current === keys[0] && event.key === keys[1])
        ) {
          // A ctrl+ action can trigger default browser behavior
          // for example ctrl+s saves the html file, we disable that behavior
          if (isCtrlKeyShortcut) e.preventDefault();

          // Prevent the space bar to scroll the page
          if (keys[0] === " ") e.preventDefault();

          if (options?.debounceTime === 0 || options?.resetOnKeyUp) {
            setTrigger(true);
            previousKey.current = "";
          } else {
            debouncedTrigger();
          }
        } else {
          previousKey.current = event.key;
        }
      }
    }

    if (shortcut) {
      refEl.addEventListener("keydown", handleKeyDown);
      if (options?.resetOnKeyUp) refEl.addEventListener("keyup", handleKeyUp);
      window.addEventListener("blur", clearTrigger, false);
      return () => {
        refEl.removeEventListener("keydown", handleKeyDown);
        if (options?.resetOnKeyUp) refEl.removeEventListener("keyup", handleKeyUp);
        window.removeEventListener("blur", clearTrigger);
      };
    }
    return () => {};
  }, [
    options?.ref,
    options?.disabled,
    options?.disableIfWindowSelection,
    options?.debounceTime,
    options?.resetOnKeyUp,
    refEl,
    shortcut,
    keys,
    isCtrlKeyShortcut,
    ignoreInputFieldFocused,
    debouncedTrigger,
    trigger,
    clearTrigger,
  ]);

  useEffect(() => {
    if (!options?.resetOnKeyUp) clearTrigger();
  }, [clearTrigger, options?.resetOnKeyUp]);

  return trigger;
}

export type UseShortcutParams = [
  Parameters<typeof useShortcut>[0]?,
  Parameters<typeof useShortcut>[1]?,
  Omit<Parameters<typeof useShortcut>[2], "ref">?,
];

export default useShortcut;
